import { memo } from 'react';

import { StarIcon } from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { Button, FilledStar, PrimaryLink, TableCell, TableRow } from '@/_components';
import { DeleteButton } from '@/_containers';
import type { InventoryBinLocationQuerySchema } from '@/_pages/settings/inventory-bin-locations/shared/schemas';

type InventoryBinLocationItemProps = {
  binLocation: InventoryBinLocationQuerySchema;
  isEditing?: boolean;
  onDelete: (binLocation: InventoryBinLocationQuerySchema) => void;
  className?: string;
  setBinLocationType?: (binLocation: InventoryBinLocationQuerySchema, isPrimary: boolean) => void;
};

function InventoryBinLocationItemBase({
  binLocation,
  isEditing = false,
  onDelete,
  className,
  setBinLocationType,
}: InventoryBinLocationItemProps) {
  const isPrimary = binLocation.pivot?.type === 'primary';

  return (
    <TableRow className={twMerge('align-center hover:bg-transparent dark:hover:bg-transparent', className)}>
      <TableCell className="w-full">
        <PrimaryLink
          to="/settings/inventory-bin-locations/$id"
          params={{ id: `${binLocation.id}` }}
          state={{ naturalBack: true }}
        >
          <div className="text-accent text-sm font-normal dark:text-white">{binLocation?.location ?? ''}</div>
        </PrimaryLink>
      </TableCell>
      <TableCell className="flex w-20 flex-row justify-end pl-0">
        <Button
          icon={isPrimary ? FilledStar : StarIcon}
          tag="div"
          as="link"
          size="sm"
          disableRipple
          aria-label="Contact Type"
          iconClassName="text-gray-400"
          className={!isPrimary ? 'opacity-40 hover:opacity-100' : undefined}
          disabled={!isEditing}
          onClick={isEditing ? () => setBinLocationType?.(binLocation, isPrimary) : undefined}
        />
        {isEditing && (
          <DeleteButton
            type="icon"
            size="sm"
            title="Remove Domain?"
            text="The Domain record will not be deleted; only its association will be removed."
            yesButton="Yes, Remove"
            aria-label="delete-client-domain"
            callback={() => onDelete(binLocation)}
          />
        )}
      </TableCell>
    </TableRow>
  );
}

export const InventoryBinLocationItem = memo(InventoryBinLocationItemBase);
